// Edit-listing mutation hooks (share + sealed dossier). Seller changes the
// asking price and/or allow_bidding of an ACTIVE listing.
//
//   POST /api/v1/marketplace/listings/share/{id}/edit
//   POST /api/v1/marketplace/listings/sealed-dossier/{id}/edit
//   body: { price_cents, allow_bidding }
//
// Mirrors useCancelShareListing in shape (idempotency key per call; branched
// error envelope). NO onSuccess invalidation — the caller owns which queries
// to refresh (my-listings, vault-items, marketplace browse, listing-live-state).
//
// Error codes: LISTING_NOT_FOUND (404, missing or not yours),
// LISTING_NOT_ACTIVE (409, sold/cancelled — a buyer won the race),
// VALIDATION_FAILED (400, sub-floor price), RATE_LIMITED (429).

import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type EditListingKind = "share" | "sealed_dossier";

export interface EditListingVariables {
  listing_id: string;
  price_cents: number;
  allow_bidding: boolean;
}

export interface EditListingResponseData {
  listing_id: string;
  price_cents: number;
  allow_bidding: boolean;
  /** "active" normally; "sold" when the price drop auto-completed a standing
   *  offer at the offered price. */
  status: string;
  /** True when lowering the ask onto a standing offer completed the trade. */
  auto_accepted: boolean;
  /** The price the item actually sold for (offer amount) when auto_accepted. */
  sold_price_cents: number | null;
}

export interface EditListingError {
  code: string;
  message: string;
  status: number;
}

function freshIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function endpointFor(kind: EditListingKind, id: string): string {
  return kind === "share"
    ? `/api/v1/marketplace/listings/share/${id}/edit`
    : `/api/v1/marketplace/listings/sealed-dossier/${id}/edit`;
}

async function postEdit(
  kind: EditListingKind,
  vars: EditListingVariables,
): Promise<EditListingResponseData> {
  const response = await fetch(endpointFor(kind, vars.listing_id), {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": freshIdempotencyKey(),
    },
    body: JSON.stringify({
      price_cents: vars.price_cents,
      allow_bidding: vars.allow_bidding,
    }),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: EditListingError = {
      code: "EDIT_LISTING_FAILED",
      message: `Edit failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          status: response.status,
        };
      }
    } catch {
      // Body not JSON — keep default.
    }
    throw detail;
  }
  const body = (await response.json()) as { data: EditListingResponseData };
  return body.data;
}

function useEditListing(
  kind: EditListingKind,
): UseMutationResult<EditListingResponseData, EditListingError, EditListingVariables> {
  return useMutation<EditListingResponseData, EditListingError, EditListingVariables>({
    mutationFn: (vars) => postEdit(kind, vars),
    // NO onSuccess — caller invalidates the relevant query keys.
  });
}

export function useEditShareListing() {
  return useEditListing("share");
}

export function useEditSealedDossierListing() {
  return useEditListing("sealed_dossier");
}
